import axios, { InternalAxiosRequestConfig } from 'axios'

import { getApiAccessToken } from '../auth/getApiAccessToken'
import { apiConfig, msalInstance } from '../authConfig'

type RetriableRequestConfig = InternalAxiosRequestConfig & { retriedWithFreshToken?: boolean }

let tokenRefreshInFlight: Promise<unknown> | null = null

const apiClient = axios.create({
  baseURL: apiConfig.webApiUri,
  headers: {
    'Content-Type': 'application/json',
  },
})

msalInstance.initialize().then(() => {
  apiClient.interceptors.request.use(async (config) => {
    const token = await getApiAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token.accessToken}`
    }
    return config
  })
})

// MSAL can serve a cached token the API already rejects (e.g. clock skew): renew it once before failing.
apiClient.interceptors.response.use(undefined, async (error) => {
  const config: RetriableRequestConfig | undefined = error?.config
  if (!axios.isAxiosError(error) || error.response?.status !== 401 || !config || config.retriedWithFreshToken) {
    throw error
  }
  config.retriedWithFreshToken = true
  try {
    if (await wasSentWithCurrentToken(config)) {
      tokenRefreshInFlight ??= getApiAccessToken({ forceRefresh: true }).finally(() => {
        tokenRefreshInFlight = null
      })
      await tokenRefreshInFlight
    }
  } catch {
    throw error
  }
  return apiClient(config)
})

async function wasSentWithCurrentToken(config: RetriableRequestConfig) {
  const currentToken = await getApiAccessToken()
  return config.headers.Authorization === `Bearer ${currentToken?.accessToken}`
}

export default apiClient
